import {
    type EnvMap,
    type EnvWarnFn,
    resolveNonNegativeIntegerEnv,
    resolvePositiveIntegerEnv,
    resolveRawTextEnv,
    resolveTokenizedEnumEnv,
    resolveTrimmedTextEnv,
    resolveTrueOnlyBooleanEnv,
    resolveUnitIntervalEnv,
} from '@/shared/env-parsing';
import {
    getAvailableOnboardingAgreementLocales,
    NEXT_PUBLIC_ONBOARDING_AGREEMENT_LOCALES_ENV_KEY,
    type OnboardingAgreementLocale,
} from '@/shared/onboarding-agreement';

export type PublicClientLogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error';

export type PublicClientConfig = Readonly<{
    websocketUrl: string;
    isProduction: boolean;
    logLevel: PublicClientLogLevel;
    onboardingAgreementLocales: readonly OnboardingAgreementLocale[];
    isAutoRowsOnPageFitEnabled: boolean;
    isChatHistoryDebugJsonEnabled: boolean;
    reviewSelectionReconcileRequestTimeoutMs: number;
    reviewSelectionReconcileStatusPollIntervalMs: number;
    reviewsDataReconciliationSelectionVisualDelayMs: number;
    launchDarklyObservability: {
        enabled: boolean;
        clientSideId: string;
        environment: string;
        tracesSampleRate: number;
        sessionReplaySampleRate: number;
        debug: boolean;
    };
}>;

const DEFAULT_REVIEW_SELECTION_RECONCILE_REQUEST_TIMEOUT_MS = 60_000;
const DEFAULT_REVIEW_SELECTION_RECONCILE_STATUS_POLL_INTERVAL_MS = 5_000;
const DEFAULT_REVIEWS_DATA_RECONCILIATION_SELECTION_VISUAL_DELAY_MS = 1_000;
const DEFAULT_LD_TRACES_SAMPLE_RATE = 0.1;
const DEFAULT_LD_SESSION_REPLAY_SAMPLE_RATE = 0.05;
const PUBLIC_CLIENT_LOG_LEVELS = ['trace', 'debug', 'info', 'warn', 'error'] as const;

const readDefaultPublicClientEnv = (): EnvMap => ({
    // NOTE: Keep literal NEXT_PUBLIC_* accesses so Next.js can inline them for browser bundles.
    // NOTE: ENABLE_CHAT_HISTORY_DEBUG_JSON is exposed to the browser via next.config.ts `env`.
    NODE_ENV: process.env.NODE_ENV,
    NEXT_PUBLIC_WEBSOCKET_URL: process.env.NEXT_PUBLIC_WEBSOCKET_URL,
    NEXT_PUBLIC_LOG_LEVEL: process.env.NEXT_PUBLIC_LOG_LEVEL,
    [NEXT_PUBLIC_ONBOARDING_AGREEMENT_LOCALES_ENV_KEY]: process.env.NEXT_PUBLIC_ONBOARDING_AGREEMENT_LOCALES,
    NEXT_PUBLIC_DISABLE_AUTO_ROWS_ON_PAGE_FIT: process.env.NEXT_PUBLIC_DISABLE_AUTO_ROWS_ON_PAGE_FIT,
    NEXT_PUBLIC_REVIEW_SELECTION_RECONCILE_REQUEST_TIMEOUT_MS:
        process.env.NEXT_PUBLIC_REVIEW_SELECTION_RECONCILE_REQUEST_TIMEOUT_MS,
    NEXT_PUBLIC_REVIEW_SELECTION_RECONCILE_STATUS_POLL_INTERVAL_MS:
        process.env.NEXT_PUBLIC_REVIEW_SELECTION_RECONCILE_STATUS_POLL_INTERVAL_MS,
    NEXT_PUBLIC_REVIEWS_DATA_RECONCILIATION_SELECTION_VISUAL_DELAY_MS:
        process.env.NEXT_PUBLIC_REVIEWS_DATA_RECONCILIATION_SELECTION_VISUAL_DELAY_MS,
    ENABLE_CHAT_HISTORY_DEBUG_JSON: process.env.ENABLE_CHAT_HISTORY_DEBUG_JSON,
    NEXT_PUBLIC_LD_CLIENT_SIDE_ID: process.env.NEXT_PUBLIC_LD_CLIENT_SIDE_ID,
    LD_OBSERVABILITY_ENABLED: process.env.LD_OBSERVABILITY_ENABLED,
    LD_OBSERVABILITY_ENV: process.env.LD_OBSERVABILITY_ENV,
    LD_TRACES_SAMPLE_RATE: process.env.LD_TRACES_SAMPLE_RATE,
    LD_SESSION_REPLAY_SAMPLE_RATE: process.env.LD_SESSION_REPLAY_SAMPLE_RATE,
    LD_OBSERVABILITY_DEBUG: process.env.LD_OBSERVABILITY_DEBUG,
});

export const getConfiguredOnboardingAgreementLocales = (
    env: EnvMap = readDefaultPublicClientEnv(),
): readonly OnboardingAgreementLocale[] =>
    getAvailableOnboardingAgreementLocales(env[NEXT_PUBLIC_ONBOARDING_AGREEMENT_LOCALES_ENV_KEY]);

export const createPublicClientConfig = ({
    env,
    warn,
}: {
    env?: EnvMap;
    warn?: EnvWarnFn;
} = {}): PublicClientConfig => {
    const resolvedEnv = env ?? readDefaultPublicClientEnv();

    const isProduction =
        resolveRawTextEnv({
            envKey: 'NODE_ENV',
            env: resolvedEnv,
            defaultValue: '',
        }) === 'production';

    const logLevel = resolveTokenizedEnumEnv({
        envKey: 'NEXT_PUBLIC_LOG_LEVEL',
        allowedValues: PUBLIC_CLIENT_LOG_LEVELS,
        env: resolvedEnv,
        defaultValue: isProduction ? 'info' : 'trace',
        policyMode: 'warn-default',
        warn,
        invalidWarning: (raw, fallback) => `[env] Invalid NEXT_PUBLIC_LOG_LEVEL="${raw}"; defaulting to ${fallback}`,
    });

    const isAutoRowsOnPageFitDisabled = resolveTrueOnlyBooleanEnv({
        envKey: 'NEXT_PUBLIC_DISABLE_AUTO_ROWS_ON_PAGE_FIT',
        env: resolvedEnv,
        defaultValue: false,
        policyMode: 'warn-default',
        warn,
        invalidWarning: (raw, defaultValue) =>
            `[env] Invalid NEXT_PUBLIC_DISABLE_AUTO_ROWS_ON_PAGE_FIT="${raw}"; defaulting to ${String(defaultValue)}`,
    });

    const isChatHistoryDebugJsonEnabled = resolveTrueOnlyBooleanEnv({
        envKey: 'ENABLE_CHAT_HISTORY_DEBUG_JSON',
        env: resolvedEnv,
        defaultValue: false,
        policyMode: 'warn-default',
        warn,
        invalidWarning: (raw, defaultValue) =>
            `[env] Invalid ENABLE_CHAT_HISTORY_DEBUG_JSON="${raw}"; defaulting to ${String(defaultValue)}`,
    });

    const reviewSelectionReconcileRequestTimeoutMs = resolvePositiveIntegerEnv({
        envKey: 'NEXT_PUBLIC_REVIEW_SELECTION_RECONCILE_REQUEST_TIMEOUT_MS',
        env: resolvedEnv,
        defaultValue: DEFAULT_REVIEW_SELECTION_RECONCILE_REQUEST_TIMEOUT_MS,
        policyMode: 'warn-default',
        warn,
        invalidWarning: (raw, fallback) =>
            `[env] Invalid NEXT_PUBLIC_REVIEW_SELECTION_RECONCILE_REQUEST_TIMEOUT_MS="${raw}"; defaulting to ${fallback}`,
    });

    const reviewSelectionReconcileStatusPollIntervalMs = resolvePositiveIntegerEnv({
        envKey: 'NEXT_PUBLIC_REVIEW_SELECTION_RECONCILE_STATUS_POLL_INTERVAL_MS',
        env: resolvedEnv,
        defaultValue: DEFAULT_REVIEW_SELECTION_RECONCILE_STATUS_POLL_INTERVAL_MS,
        policyMode: 'warn-default',
        warn,
        invalidWarning: (raw, fallback) =>
            `[env] Invalid NEXT_PUBLIC_REVIEW_SELECTION_RECONCILE_STATUS_POLL_INTERVAL_MS="${raw}"; defaulting to ${fallback}`,
    });

    const reviewsDataReconciliationSelectionVisualDelayMs = resolveNonNegativeIntegerEnv({
        envKey: 'NEXT_PUBLIC_REVIEWS_DATA_RECONCILIATION_SELECTION_VISUAL_DELAY_MS',
        env: resolvedEnv,
        defaultValue: DEFAULT_REVIEWS_DATA_RECONCILIATION_SELECTION_VISUAL_DELAY_MS,
        policyMode: 'warn-default',
        warn,
        invalidWarning: (raw, fallback) =>
            `[env] Invalid NEXT_PUBLIC_REVIEWS_DATA_RECONCILIATION_SELECTION_VISUAL_DELAY_MS="${raw}"; defaulting to ${fallback}`,
    });

    const launchDarklyObservability = {
        enabled: resolveTrueOnlyBooleanEnv({
            envKey: 'LD_OBSERVABILITY_ENABLED',
            env: resolvedEnv,
            defaultValue: false,
            policyMode: 'warn-default',
            warn,
            invalidWarning: (raw, fallback) =>
                `[env] Invalid LD_OBSERVABILITY_ENABLED="${raw}"; defaulting to ${String(fallback)}`,
        }),
        clientSideId: resolveTrimmedTextEnv({
            envKey: 'NEXT_PUBLIC_LD_CLIENT_SIDE_ID',
            env: resolvedEnv,
            defaultValue: '',
        }),
        environment: resolveTrimmedTextEnv({
            envKey: 'LD_OBSERVABILITY_ENV',
            env: resolvedEnv,
            defaultValue: isProduction ? 'production' : 'development',
        }),
        tracesSampleRate: resolveUnitIntervalEnv({
            envKey: 'LD_TRACES_SAMPLE_RATE',
            env: resolvedEnv,
            defaultValue: DEFAULT_LD_TRACES_SAMPLE_RATE,
            policyMode: 'warn-default',
            warn,
            invalidWarning: (raw, fallback) =>
                `[env] Invalid LD_TRACES_SAMPLE_RATE="${raw}"; defaulting to ${fallback}`,
        }),
        sessionReplaySampleRate: resolveUnitIntervalEnv({
            envKey: 'LD_SESSION_REPLAY_SAMPLE_RATE',
            env: resolvedEnv,
            defaultValue: DEFAULT_LD_SESSION_REPLAY_SAMPLE_RATE,
            policyMode: 'warn-default',
            warn,
            invalidWarning: (raw, fallback) =>
                `[env] Invalid LD_SESSION_REPLAY_SAMPLE_RATE="${raw}"; defaulting to ${fallback}`,
        }),
        debug: resolveTrueOnlyBooleanEnv({
            envKey: 'LD_OBSERVABILITY_DEBUG',
            env: resolvedEnv,
            defaultValue: false,
            policyMode: 'warn-default',
            warn,
            invalidWarning: (raw, fallback) =>
                `[env] Invalid LD_OBSERVABILITY_DEBUG="${raw}"; defaulting to ${String(fallback)}`,
        }),
    } as const;

    const config = {
        websocketUrl: resolveTrimmedTextEnv({
            envKey: 'NEXT_PUBLIC_WEBSOCKET_URL',
            env: resolvedEnv,
            defaultValue: '',
        }),
        isProduction,
        logLevel,
        onboardingAgreementLocales: getConfiguredOnboardingAgreementLocales(resolvedEnv),
        isAutoRowsOnPageFitEnabled: !isAutoRowsOnPageFitDisabled,
        isChatHistoryDebugJsonEnabled,
        reviewSelectionReconcileRequestTimeoutMs,
        reviewSelectionReconcileStatusPollIntervalMs,
        reviewsDataReconciliationSelectionVisualDelayMs,
        launchDarklyObservability,
    } satisfies PublicClientConfig;

    return Object.freeze(config);
};

export const publicClientConfig = createPublicClientConfig();
